import React, { useState } from 'react';
import { BluetoothConnector } from './components/BluetoothConnector';
import { WorkoutBuilder } from './components/WorkoutBuilder';
import { WorkoutRunner } from './components/WorkoutRunner';
import { BrowserCompatibilityBanner } from './components/BrowserCompatibilityBanner';
import { getStravaSettings, StravaSettings } from './lib/stravaSettings';
import { Logo } from './components/Logo';
import { StravaAuth } from './components/StravaAuth';
import OfflineIndicator from './components/OfflineIndicator';
import InstallPrompt from './components/InstallPrompt';
import { Workout } from './types/workout';

export default function App() {
  const [workout, setWorkout] = useState<Workout | null>(null);
  const [connectedToTrainer, setConnectedToTrainer] = useState(false);
  const [connectedToHeartRate, setConnectedToHeartRate] = useState(false);
  const [cadenceEnabled, setCadenceEnabled] = useState(false);
  const [stravaConnected, setStravaConnected] = useState(false);
  const [stravaSettings, setStravaSettings] = useState<StravaSettings>(getStravaSettings());

  return (
    <div className="min-h-screen p-4 bg-gray-50">
      <OfflineIndicator />

      <div className="flex items-center mb-6">
        <Logo size="small" />
      </div>

      {!workout && (
        <div className="mb-6 flex flex-col lg:flex-row lg:items-start lg:justify-between lg:space-x-4">
          <div className="mb-4 lg:mb-0 lg:max-w-[800px]">
            <p className="text-gray-600 text-sm">
              Free smart trainer control app for indoor cycling. Connect your
              ERG trainer via Bluetooth to set power targets, monitor heart rate
              and cadence, create custom interval workouts, and automatically
              upload rides to Strava. Compatible with Wahoo, Tacx, Elite, and
              other smart trainers.
            </p>
          </div>
          <div className="flex-shrink-0 lg:min-w-fit">
            <StravaAuth
              onAuthChange={(isAuthenticated) => {
                setStravaConnected(isAuthenticated);
              }}
              onSettingsChange={(settings) => {
                setStravaSettings(settings);
              }}
            />
          </div>
        </div>
      )}

      <BrowserCompatibilityBanner />

      <BluetoothConnector
        connected={connectedToTrainer}
        setConnected={setConnectedToTrainer}
        hrConnected={connectedToHeartRate}
        setHrConnected={setConnectedToHeartRate}
        cadenceEnabled={cadenceEnabled}
        setCadenceEnabled={setCadenceEnabled}
      />

      {!workout ? (
        <WorkoutBuilder
          onStart={setWorkout}
          isConnectedToTrainer={connectedToTrainer}
        />
      ) : (
        <WorkoutRunner
          workout={workout}
          onReset={() => setWorkout(null)}
          isHeartRateConnected={connectedToHeartRate}
          isCadenceEnabled={cadenceEnabled}
          isStravaConnected={stravaConnected}
          stravaSettings={stravaSettings}
        />
      )}

      <InstallPrompt isWorkoutRunning={!!workout} />
    </div>
  );
}
